import { useTranslation } from "react-i18next";
import { useCallback, useState, useEffect, createContext, useContext, type ReactNode } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface ConfirmCtx {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────
const ConfirmContext = createContext<ConfirmCtx | null>(null);

// ─── Imperative bridge ─────────────────────────────────────────────────────────
// Lets code outside the React tree (e.g. the admin-ui `Modal.confirm` / `Popconfirm`
// shims) request a styled confirm dialog without needing the hook. Mirrors the
// `pushToast` bridge pattern in Toast.tsx. The provider below is the only subscriber.
let imperativeConfirm: ((options: ConfirmOptions) => Promise<boolean>) | null = null;

export function pushConfirm(options: ConfirmOptions): Promise<boolean> {
  if (imperativeConfirm) return imperativeConfirm(options);
  // Fallback if the provider hasn't mounted yet (should not happen in practice).
  return Promise.resolve(window.confirm(`${options.title ?? ""}\n${options.description ?? ""}`));
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...options, resolve });
    });
  }, []);

  useEffect(() => {
    imperativeConfirm = confirm;
    return () => {
      if (imperativeConfirm === confirm) imperativeConfirm = null;
    };
  }, [confirm]);

  const close = (value: boolean) => {
    pending?.resolve(value);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {pending && (
        <div
          className="admin-overlay admin-confirm"
          style={{
            position: "fixed", inset: 0, zIndex: 10000,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(0,0,0,0.4)",
            padding: 16,
          }}
          onClick={() => close(false)}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 24,
              maxWidth: 360,
              width: "100%",
              boxShadow: "0 12px 32px rgba(0,0,0,0.24)",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
              {pending.title}
            </div>
            {pending.description && (
              <div style={{ fontSize: 14, color: "#6B7280", lineHeight: "20px", marginBottom: 20 }}>
                {pending.description}
              </div>
            )}
            <div className="admin-modal-footer" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => close(false)}
                style={{
                  padding: "10px 16px", borderRadius: 10, border: "1px solid #E5E7EB",
                  background: "#fff", color: "#374151", fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                {pending.cancelLabel ?? t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                style={{
                  padding: "10px 16px", borderRadius: 10, border: "none",
                  background: pending.danger ? "#D93025" : "#00731E",
                  color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                {pending.confirmLabel ?? t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useConfirmDialog(): ConfirmCtx {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirmDialog must be used inside <ConfirmDialogProvider>");
  return ctx;
}
